import { useEffect, useRef, useState } from 'react';
import { Section } from '@vnetwork/ui/layout';
import { Skeleton, Textarea } from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import { useGlobalConfig, useUpdateGlobalConfig, withConfig } from '@/api/waap';
import { errMessage } from '../waap-util';
import { Labeled, SaveBar, ToggleRow } from './settings-ui';
import styles from './VerifiedBotSettings.module.css';

/** Split a textarea into trimmed, non-empty user-agent entries. */
function toList(text: string): string[] {
  return text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Verified Bot — when enabled, the edge directly forwards verified bots
 * identified by user agent. Backed by the global-config `good_bots` slice
 * (`verified_by_ua_enabled` + the `ua_list_str`/`ua_list` pair).
 */
export function VerifiedBotSettings({ domainId }: { domainId: string }) {
  const { show } = useToast();
  const { data: record, isLoading } = useGlobalConfig(domainId);

  const [enabled, setEnabled] = useState(false);
  const [text, setText] = useState('');
  // Seed from the server only when the record actually changes (by `updated`),
  // so a background refetch can't clobber unsaved edits.
  const syncedUpdated = useRef<number | null>(null);
  useEffect(() => {
    if (!record || record.updated === syncedUpdated.current) return;
    syncedUpdated.current = record.updated;
    const gb = record.config.good_bots;
    setEnabled(gb.verified_by_ua_enabled);
    setText(gb.ua_list_str || gb.ua_list.join('\n'));
  }, [record]);

  const update = useUpdateGlobalConfig(domainId, {
    onSuccess: () => show({ tone: 'success', title: 'Verified Bot saved' }),
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });

  const save = () => {
    if (!record) return;
    const list = toList(text);
    update.mutate(
      withConfig(record, {
        good_bots: {
          ...record.config.good_bots,
          verified_by_ua_enabled: enabled,
          ua_list_str: text,
          ua_list: list,
        },
      }),
    );
  };

  if (isLoading) return <Skeleton className={styles.skeleton} />;

  return (
    <Section
      title="Verified Bot"
      description="When enabled, the system will directly forward verified bots (identified through configured user agents)."
    >
      <ToggleRow
        title="Verified Bot by User Agent"
        description="Directly forward bots whose user agent matches the list below."
        checked={enabled}
        onChange={setEnabled}
      />

      {enabled && (
        <div className={styles.fields}>
          <Labeled
            label="User agents"
            hint="Enter the verified bot's user agent OR the regex of the bot's user agent. Can enter 1-256 characters for each entry. Can enter 1-10 entries separated by line break."
          >
            <Textarea
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={'Googlebot\nbingbot'}
            />
          </Labeled>
        </div>
      )}

      <SaveBar onSave={save} saving={update.isPending} disabled={!record} />
    </Section>
  );
}
